'use client';

import { useState } from 'react';
import { PieChart, Pie, Sector, ResponsiveContainer } from 'recharts';
import { SentimentCount, CategoryCount } from '../types';
import { getSentimentColorValue } from '../utils/styleUtils';

interface CombinedPieChartProps {
  sentimentData: SentimentCount[];
  categoryData: CategoryCount[];
  brandData?: CategoryCount[]; // Brand data uses same structure as category
  sourceData?: CategoryCount[]; // Source data uses same structure as category
  activeChart: 'sentiment' | 'category' | 'brand' | 'source';
  onSentimentClick: (sentiment: string) => void;
  onCategoryClick: (category: string) => void;
  onBrandClick?: (brand: string) => void;
  onSourceClick?: (source: string) => void;
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 5) * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 15) * cos;
  const my = cy + (outerRadius + 15) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 15;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  // Get the appropriate label key based on the payload object
  const labelKey = payload.sentiment !== undefined ? 'sentiment' : 
                   payload.category !== undefined ? 'category' : 
                   payload.brand !== undefined ? 'brand' : 'source';
  
  // Get the label text
  const labelText = payload[labelKey];

  return (
    <g>
      {/* Center text with tspan for text wrapping if needed */}
      <text x={cx} y={cy - 5} textAnchor="middle" fill={fill} fontSize="11">
        {labelText.length > 12 ? (
          <>
            <tspan x={cx} dy="0">{labelText.substring(0, labelText.indexOf(' ') > -1 ? labelText.indexOf(' ') : labelText.length)}</tspan>
            <tspan x={cx} dy="12">{labelText.indexOf(' ') > -1 ? labelText.substring(labelText.indexOf(' ') + 1) : ''}</tspan>
          </>
        ) : (
          labelText
        )}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 3}
        outerRadius={outerRadius + 5}
        fill={fill}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} textAnchor={textAnchor} fill="#333" fontSize="10" fontWeight="normal">{value}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} dy={12} textAnchor={textAnchor} fill="#666" fontSize="8">
        {`(${Math.round(percent * 100)}%)`}
      </text>
    </g>
  );
};

export const CombinedPieChart = ({ 
  sentimentData, 
  categoryData,
  brandData = [],
  sourceData = [],
  activeChart,
  onSentimentClick, 
  onCategoryClick,
  onBrandClick = () => {},
  onSourceClick = () => {}
}: CombinedPieChartProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Choose data based on the active chart
  const currentData = activeChart === 'sentiment' ? sentimentData : 
                      activeChart === 'category' ? categoryData : 
                      activeChart === 'brand' ? brandData : sourceData;
  
  // Calculate total for percentages
  const total = currentData.reduce((sum, item) => sum + item.count, 0);
  
  if (total === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }
  
  // Transform the data for recharts
  const data = currentData.map(item => {
    if (activeChart === 'sentiment') {
      const sentimentItem = item as SentimentCount;
      return {
        sentiment: sentimentItem.sentiment,
        value: sentimentItem.count,
        fill: getSentimentColorValue(sentimentItem.sentiment)
      };
    } else if (activeChart === 'category') {
      const categoryItem = item as CategoryCount;
      return {
        category: categoryItem.category,
        value: categoryItem.count,
        fill: categoryItem.color
      };
    } else if (activeChart === 'brand') {
      const brandItem = item as CategoryCount;
      // Make sure brands show with reasonable values in the pie chart
      return {
        brand: brandItem.category,
        value: Math.max(brandItem.count, Math.ceil(total * 0.05)), // Ensure at least 5% of total value for visibility
        fill: brandItem.color
      };
    } else {
      const sourceItem = item as CategoryCount;
      return {
        source: sourceItem.category,
        value: sourceItem.count,
        fill: sourceItem.color
      };
    }
  });

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  // Handle item click based on the active chart
  const handleItemClick = (data: any) => {
    if (activeChart === 'sentiment' && data.sentiment) {
      onSentimentClick(data.sentiment);
    } else if (activeChart === 'category' && data.category) {
      onCategoryClick(data.category);
    } else if (activeChart === 'brand' && data.brand) {
      onBrandClick(data.brand);
    } else if (activeChart === 'source' && data.source) {
      onSourceClick(data.source);
    }
  };

  return (
    <div className="relative h-full w-full flex flex-col items-center justify-center">
      <div className="h-[320px] w-full flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={42}
              outerRadius={66}
              dataKey="value"
              onMouseEnter={onPieEnter}
              onClick={handleItemClick}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}; 